import { skipToken } from '@reduxjs/toolkit/query';
import { Form } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  getRecommendChatErrorCode,
  getRecommendChatErrorMessage,
  useGetRecommendChatBindingConflictsQuery,
  useGetRecommendChatSettingsQuery,
  useSaveRecommendChatSettingsMutation,
} from '@app/api/RecommendChat';
import type {
  RecommendChatSettings,
  RecommendChatSettingsValues,
} from '@app/api/RecommendChat';
import { SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNewUserAccessRights } from '@app/hooks/useNewUserAccessRights';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import { useLoyaltyChatSettingsModalEffects } from './useLoyaltyChatSettingsModalEffects';

interface UseLoyaltyChatSettingsModalParams {
  integrationId: number;
  onSaved(): void | Promise<unknown>;
}

const toSettingsValues = ({
  options: _options,
  ...values
}: RecommendChatSettings): RecommendChatSettingsValues => values;

export const useLoyaltyChatSettingsModal = ({
  integrationId,
  onSaved,
}: UseLoyaltyChatSettingsModalParams) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const {
    userAccessRights: { Integrations },
  } = useNewUserAccessRights();
  const canEdit = Integrations.IsOn && Integrations.IsEdit;
  const [form] = Form.useForm<RecommendChatSettingsValues>();
  const { showNotification } = useNotification();
  const [snapshot, setSnapshot] = useState<RecommendChatSettings | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasVersionConflict, setHasVersionConflict] = useState(false);
  const initializedRef = useRef(false);
  const submitRef = useRef(false);
  const args = useMemo(
    () =>
      canEdit && ActiveBranchId && integrationId > 0
        ? { ActiveBranchId, integrationId }
        : skipToken,
    [ActiveBranchId, canEdit, integrationId]
  );
  const {
    data: settings,
    isFetching,
    error: queryError,
    refetch,
  } = useGetRecommendChatSettingsQuery(args, {
    refetchOnMountOrArgChange: true,
  });
  const [saveSettings, { isLoading: isSaving }] =
    useSaveRecommendChatSettingsMutation();
  const canResolveBindings =
    snapshot?.options.canResolveBindings === true && canEdit;
  const {
    data: conflicts = [],
    isFetching: isConflictsLoading,
    error: conflictsError,
    refetch: refetchConflicts,
  } = useGetRecommendChatBindingConflictsQuery(
    canResolveBindings ? args : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const initializeHandler = useCallback(
    (value: RecommendChatSettings) => {
      if (initializedRef.current || value.integrationId !== integrationId)
        return;
      initializedRef.current = true;
      setSnapshot(value);
      form.setFieldsValue(toSettingsValues(value));
    },
    [form, integrationId]
  );
  useLoyaltyChatSettingsModalEffects({
    settings,
    onInitialize: initializeHandler,
  });

  const operatorOptions = useMemo(
    () =>
      snapshot?.options.operators.map(({ id, name }) => ({
        value: id,
        label: name,
      })) ?? [],
    [snapshot]
  );

  const reloadHandler = useCallback(async () => {
    if (args === skipToken) return;
    try {
      const fresh = await refetch().unwrap();
      setSnapshot(fresh);
      form.setFieldsValue(toSettingsValues(fresh));
      setSaveError(null);
      setHasVersionConflict(false);
    } catch (error) {
      setSaveError(getRecommendChatErrorMessage(error));
    }
  }, [args, form, refetch]);

  const saveHandler = useCallback(
    async (values: RecommendChatSettingsValues) => {
      if (
        submitRef.current ||
        !canEdit ||
        !ActiveBranchId ||
        !snapshot ||
        hasVersionConflict
      )
        return;
      const operators = Array.from(new Set(values.operatorUserIds ?? []));
      if (
        operators.some(
          (id) => !snapshot.options.operators.some((option) => option.id === id)
        )
      ) {
        setSaveError('Выберите доступных операторов.');
        return;
      }
      submitRef.current = true;
      setSaveError(null);
      try {
        const saved = await saveSettings({
          ActiveBranchId,
          data: {
            integrationId,
            configurationVersion: snapshot.configurationVersion,
            chatEnabled: values.chatEnabled,
            operatorUserIds: operators,
          },
        }).unwrap();
        setSnapshot(saved);
        form.setFieldsValue(toSettingsValues(saved));
        showNotification('Настройки Рекомендуй сохранены', SUCCESS);
        await onSaved();
      } catch (error) {
        setSaveError(getRecommendChatErrorMessage(error));
        setHasVersionConflict(
          getRecommendChatErrorCode(error) === 'VERSION_CONFLICT'
        );
      } finally {
        submitRef.current = false;
      }
    },
    [
      ActiveBranchId,
      canEdit,
      form,
      hasVersionConflict,
      integrationId,
      onSaved,
      saveSettings,
      showNotification,
      snapshot,
    ]
  );

  return {
    form,
    snapshot,
    isFetching,
    isSaving,
    canEdit,
    operatorOptions,
    reloadHandler,
    saveHandler,
    errorMessage:
      saveError ??
      (queryError ? getRecommendChatErrorMessage(queryError) : null),
    hasVersionConflict,
    canResolveBindings,
    conflicts,
    isConflictsLoading,
    conflictsError: conflictsError
      ? getRecommendChatErrorMessage(conflictsError)
      : null,
    refetchConflicts,
  };
};
